<script lang="ts">
  import GhostButton from "../../../../../_shared/_modules/ghost-button"
  import { fetchContactPolicy } from "../../../../../../api/kehrblatt/contact-policy"
  import {
    fetchOrganizationBusinessProfile,
    type KehrblattBusinessProfile,
  } from "../../../../../../api/kehrblatt/business-profile"
  import type { OrgaViewFrameContext } from "../../../../context"
  import { getTabs, resolveTemplateSelection } from "./config"
  import EmailPage from "./pages/Email"
  import SmsPage from "./pages/Sms"
  import WhatsAppPage from "./pages/WhatsApp"
  import PhonePage from "./pages/Phone"
  import LetterTemplatesEditor from "./pages/Letter"

  const { viewFrameContext, activeTab } = $props<{
    viewFrameContext: OrgaViewFrameContext
    activeTab: string
  }>()
  const organizationId = $derived(
    viewFrameContext.organization?.id ?? viewFrameContext.organizationId ?? "",
  )
  const canManage = $derived(viewFrameContext.permissions.allows("core.org_settings", "manage"))
  const canView = $derived(
    canManage || viewFrameContext.permissions.allows("core.org_settings", "view"),
  )
  const selection = $derived(
    getTabs(viewFrameContext).some((tab) => tab.id === activeTab)
      ? resolveTemplateSelection(activeTab)
      : null,
  )
  let policy = $state<OrganizationContactPolicy | null>(null)
  let profile = $state<KehrblattBusinessProfile | null>(null)
  let loading = $state(false)
  let error = $state<string | null>(null)
  let generation = 0

  const load = async (targetOrganization: string) => {
    const request = ++generation
    policy = null
    profile = null
    loading = true
    error = null
    try {
      const [nextPolicy, nextProfile] = await Promise.all([
        fetchContactPolicy(targetOrganization),
        fetchOrganizationBusinessProfile(targetOrganization),
      ])

      if (request !== generation || targetOrganization !== organizationId) return

      policy = nextPolicy
      profile = nextProfile.profile
    } catch {
      if (request === generation) error = "Ankündigungsvorlagen konnten nicht geladen werden."
    } finally {
      if (request === generation) loading = false
    }
  }

  $effect(() => {
    const targetOrganization = organizationId
    const allowed = canView
    policy = null
    profile = null
    error = null
    loading = false

    if (targetOrganization && allowed) void load(targetOrganization)

    return () => {
      generation += 1
    }
  })
</script>

{#if !canView || !selection}
  <p>Diese Ankündigungsvorlage ist nicht verfügbar.</p>
{:else if error}
  <div class="state-block">
    <p role="alert">{error}</p>
    <GhostButton onclick={() => load(organizationId)}>Erneut laden</GhostButton>
  </div>
{:else if loading || !policy}
  <p role="status">Ankündigungsvorlagen werden geladen.</p>
{:else}
  {#key `${organizationId}-${activeTab}`}
    {#if selection.channel === "letter"}
      <LetterTemplatesEditor
        {organizationId}
        {policy}
        {profile}
        {canManage}
        activeTab={selection.purpose as "announcement" | "reschedule" | "aushang" | "other"}
        onPolicyUpdated={(updated) => {
          policy = updated
        }}
      />
    {:else if selection.channel === "email"}
      <EmailPage
        {organizationId}
        {policy}
        {profile}
        {canManage}
        {activeTab}
        onPolicyUpdated={(updated) => {
          policy = updated
        }}
      />
    {:else if selection.channel === "sms"}
      <SmsPage
        {organizationId}
        {policy}
        {profile}
        {canManage}
        {activeTab}
        onPolicyUpdated={(updated) => {
          policy = updated
        }}
      />
    {:else if selection.channel === "whatsapp"}
      <WhatsAppPage
        {organizationId}
        {policy}
        {profile}
        {canManage}
        {activeTab}
        onPolicyUpdated={(updated) => {
          policy = updated
        }}
      />
    {:else if selection.channel === "phone"}
      <PhonePage
        {organizationId}
        {policy}
        {profile}
        {canManage}
        {activeTab}
        onPolicyUpdated={(updated) => {
          policy = updated
        }}
      />
    {/if}
  {/key}
{/if}

<style>
  .state-block {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    flex-wrap: wrap;
  }
</style>
